import { FormEvent, useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import {
  CompanyCalendarEvent,
  createCompanyCalendarEvent,
  deleteCompanyCalendarEvent,
  updateCompanyCalendarEvent,
} from '../../api/calendarEvent';
import { reportError } from '../../utils/notify';
import './CalendarEventModal.css';

interface CalendarEventModalProps {
  mode: 'create' | 'edit';
  date: string;
  event?: CompanyCalendarEvent | null;
  onClose: () => void;
  onSaved: () => void;
}

function formatDateLabel(iso: string) {
  const [y, m, d] = iso.split('-').map(Number);
  if (!y || !m || !d) return iso;
  const wd = ['일', '월', '화', '수', '목', '금', '토'][new Date(y, m - 1, d).getDay()];
  return `${y}년 ${m}월 ${d}일 (${wd})`;
}

function CalendarEventModal({ mode, date, event, onClose, onSaved }: CalendarEventModalProps) {
  const [eventDate, setEventDate] = useState(date);
  const [title, setTitle] = useState('');
  const [content, setContent] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    setEventDate(event?.eventDate?.slice(0, 10) || date);
    setTitle(event?.title || '');
    setContent(event?.content || '');
    setError('');
  }, [date, event]);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    const trimmed = title.trim();
    if (!trimmed) {
      reportError(setError, '제목을 입력해 주세요.');
      return;
    }
    if (!eventDate) {
      reportError(setError, '날짜를 선택해 주세요.');
      return;
    }
    try {
      setSaving(true);
      setError('');
      const payload = {
        eventDate,
        title: trimmed,
        content: content.trim() || undefined,
        eventType: 'meeting',
      };
      if (mode === 'edit' && event?.eventId != null) {
        await updateCompanyCalendarEvent(event.eventId, payload);
      } else {
        await createCompanyCalendarEvent(payload);
      }
      onSaved();
      onClose();
    } catch (err: unknown) {
      const message =
        (err as { response?: { data?: { message?: string } } })?.response?.data?.message ??
        '일정 저장에 실패했습니다.';
      reportError(setError, message);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (event?.eventId == null) return;
    if (!window.confirm('이 일정을 삭제하시겠습니까?')) return;
    try {
      setSaving(true);
      setError('');
      await deleteCompanyCalendarEvent(event.eventId);
      onSaved();
      onClose();
    } catch (err: unknown) {
      const message =
        (err as { response?: { data?: { message?: string } } })?.response?.data?.message ??
        '일정 삭제에 실패했습니다.';
      reportError(setError, message);
    } finally {
      setSaving(false);
    }
  };

  return createPortal(
    <div className="cal-event-modal__backdrop" role="presentation" onClick={onClose}>
      <form
        className="cal-event-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="cal-event-modal-title"
        onClick={(e) => e.stopPropagation()}
        onSubmit={(e) => void handleSubmit(e)}
      >
        <div className="cal-event-modal__head">
          <div>
            <p className="cal-event-modal__eyebrow">CALENDAR</p>
            <h3 id="cal-event-modal-title">{mode === 'edit' ? '일정 수정' : '일정 등록'}</h3>
            <p className="cal-event-modal__sub">{formatDateLabel(eventDate)}</p>
          </div>
          <button type="button" className="cal-event-modal__close" onClick={onClose} aria-label="닫기">
            ×
          </button>
        </div>

        <div className="cal-event-modal__body">
          <label>
            날짜
            <input
              type="date"
              value={eventDate}
              onChange={(e) => setEventDate(e.target.value)}
              required
            />
          </label>
          <label>
            제목
            <input
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              maxLength={200}
              placeholder="일정 제목"
              autoFocus
              required
            />
          </label>
          <label>
            내용 <span className="cal-event-modal__optional">선택</span>
            <textarea
              value={content}
              onChange={(e) => setContent(e.target.value)}
              maxLength={2000}
              placeholder="메모를 입력하세요"
            />
          </label>
          {error ? <p className="cal-event-modal__error">{error}</p> : null}
        </div>

        <div className="cal-event-modal__actions">
          {mode === 'edit' ? (
            <button type="button" className="is-danger" disabled={saving} onClick={() => void handleDelete()}>
              삭제
            </button>
          ) : null}
          <button type="button" disabled={saving} onClick={onClose}>
            취소
          </button>
          <button type="submit" className="is-primary" disabled={saving}>
            {saving ? '저장 중...' : '저장'}
          </button>
        </div>
      </form>
    </div>,
    document.body
  );
}

export default CalendarEventModal;
